import React, { useState } from 'react';
import { ArrowLeft, Shield, Cookie, Eye, Settings, Globe, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CookiePolicy = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleReturnHome = () => {
    navigate('/#home');
  };

  const handleContactClick = () => {
    navigate('/#contact');
  };

  const cookieTypes = [
    {
      id: 'essential',
      title: 'Essential Cookies',
      icon: Shield,
      description: 'Required for basic website functionality',
      details: 'These cookies are strictly necessary for the operation of our site. They enable core functionality such as security, network management, and accessibility. Without these cookies, services you have asked for cannot be provided.',
      examples: [
        'Authentication tokens',
        'Session identifiers', 
        'Security cookies',
        'Load balancing cookies'
      ],
      retention: 'Session or up to 1 year'
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      icon: Eye,
      description: 'Help us understand how visitors interact with our website',
      details: 'These cookies collect information about how you use our website, such as which pages you visit and any errors you encounter. This data helps us improve our website performance and user experience.',
      examples: [
        'Google Analytics (_ga, _gid)',
        'Page view tracking',
        'User journey analysis',
        'Performance metrics'
      ],
      retention: '2 years maximum'
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      icon: Settings,
      description: 'Remember your preferences and provide enhanced features',
      details: 'These cookies allow our website to remember choices you make and provide enhanced, more personal features. They may be set by us or by third-party providers whose services we have added to our pages.',
      examples: [
        'Language preferences',
        'Theme settings',
        'Login credentials (optional)',
        'Form data persistence'
      ],
      retention: '1 year maximum'
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      icon: Globe,
      description: 'Used to deliver relevant advertisements and track campaign performance',
      details: 'These cookies track your browsing habits to enable us to show advertising which is more likely to be of interest to you. They may be used to build a profile of your interests and show you relevant adverts on other sites.',
      examples: [
        'Google Ads conversion tracking',
        'Facebook Pixel',
        'Retargeting cookies',
        'Campaign attribution'
      ],
      retention: '90 days to 2 years'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <button 
            onClick={handleReturnHome}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Cookie className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl font-light text-gray-900 mb-4">Cookie Policy</h1>
            <p className="text-lg text-gray-600 mb-6">
              Learn how Manifest Illusions uses cookies and similar technologies to enhance your browsing experience
            </p>
            <div className="inline-flex items-center text-sm text-gray-500">
              <span>Last Updated: June 28, 2025</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Table of Contents */}
          <div className="lg:col-span-1">
            <div className="sticky top-32">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Contents</h3>
              <nav className="space-y-2">
                <a href="#overview" className="block text-sm text-gray-600 hover:text-gray-900 transition-colors">Overview</a>
                <a href="#cookie-types" className="block text-sm text-gray-600 hover:text-gray-900 transition-colors">Cookie Types</a>
                <a href="#third-party" className="block text-sm text-gray-600 hover:text-gray-900 transition-colors">Third-Party Services</a>
                <a href="#manage" className="block text-sm text-gray-600 hover:text-gray-900 transition-colors">Managing Cookies</a>
                <a href="#rights" className="block text-sm text-gray-600 hover:text-gray-900 transition-colors">Your Rights</a>
                <a href="#changes" className="block text-sm text-gray-600 hover:text-gray-900 transition-colors">Policy Updates</a>
                <a href="#contact" className="block text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact Us</a>
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3 space-y-16">
            {/* Overview */}
            <section id="overview">
              <h2 className="text-2xl font-light text-gray-900 mb-6">What Are Cookies?</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Cookies are small text files placed on your device to store data that helps enhance your browsing experience. Some cookies are essential for our website to function properly, while others help us understand how you use our site so we can improve it.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  This Cookie Policy explains how Manifest Illusions uses cookies and similar technologies on our website, what information they collect, and how you can control them.
                </p>
              </div>
            </section>

            {/* Cookie Types */}
            <section id="cookie-types">
              <h2 className="text-2xl font-light text-gray-900 mb-8">Types of Cookies We Use</h2>
              <div className="space-y-6">
                {cookieTypes.map((cookie) => {
                  const Icon = cookie.icon;
                  const isExpanded = expandedSections[cookie.id];
                  
                  return (
                    <div key={cookie.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection(cookie.id)}
                        className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              <Icon className="w-5 h-5 text-gray-600" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">{cookie.title}</h3>
                              <p className="text-sm text-gray-600">{cookie.description}</p>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </button>
                      
                      {isExpanded && (
                        <div className="px-6 pb-6 border-t border-gray-100">
                          <div className="pt-4 space-y-4">
                            <p className="text-gray-700">{cookie.details}</p>
                            
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Examples:</h4>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {cookie.examples.map((example, index) => (
                                  <li key={index} className="flex items-center">
                                    <div className="w-1 h-1 bg-gray-400 rounded-full mr-3"></div>
                                    {example}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div className="pt-2 border-t border-gray-100">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Retention Period</span>
                              <p className="text-sm text-gray-700 mt-1">{cookie.retention}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Third-Party Services */}
            <section id="third-party">
              <h2 className="text-2xl font-light text-gray-900 mb-6">Third-Party Services</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-700 mb-4">
                  We use trusted third-party services that may set their own cookies:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Payment Processing</h4>
                    <p className="text-sm text-gray-600">Stripe and PayPal for secure transactions</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Analytics</h4>
                    <p className="text-sm text-gray-600">Google Analytics for site performance</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Hosting</h4>
                    <p className="text-sm text-gray-600">Netlify for website delivery</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Security</h4>
                    <p className="text-sm text-gray-600">Cloudflare for protection and performance</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Managing Cookies */}
            <section id="manage">
              <h2 className="text-2xl font-light text-gray-900 mb-6">Managing Your Cookie Preferences</h2>
              <p className="text-gray-700 mb-6">
                You have control over which cookies are set on your device. You can disable cookies via your browser settings, though this may limit some website functionality.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-amber-800 mb-1">Important Note</h4>
                    <p className="text-sm text-amber-700">
                      Disabling essential cookies may prevent you from logging in, making purchases, or accessing certain features of our website.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-medium text-gray-900 mb-4">Browser Instructions</h3>
              <div className="space-y-3">
                <a 
                  href="https://support.google.com/chrome/answer/95647" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className="text-gray-700 group-hover:text-gray-900">Manage cookies in Chrome</span>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                </a>
                <a 
                  href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className="text-gray-700 group-hover:text-gray-900">Manage cookies in Safari</span>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                </a>
                <a 
                  href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className="text-gray-700 group-hover:text-gray-900">Manage cookies in Firefox</span>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                </a>
              </div>
            </section>

            {/* Your Rights */}
            <section id="rights">
              <h2 className="text-2xl font-light text-gray-900 mb-6">Do Not Track (DNT)</h2>
              <p className="text-gray-700 mb-6">
                We currently do not respond to "Do Not Track" browser signals. However, you have full control over cookie settings through your browser preferences and the methods outlined above.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-blue-900 mb-3">Your Data Rights</h3>
                <p className="text-blue-800 text-sm leading-relaxed">
                  Under applicable privacy laws, you may have rights regarding your personal data, including the right to access, correct, delete, or restrict processing. Contact us if you wish to exercise these rights.
                </p>
              </div>
            </section>

            {/* Changes */}
            <section id="changes">
              <h2 className="text-2xl font-light text-gray-900 mb-6">Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Cookie Policy periodically to reflect changes in our practices or for legal and regulatory reasons. The most current version will always be available on this page, with the revision date clearly indicated at the top.
              </p>
            </section>

            {/* Contact */}
            <section id="contact">
              <h2 className="text-2xl font-light text-gray-900 mb-6">Questions or Concerns?</h2>
              <p className="text-gray-700 mb-6">
                If you have any questions about our use of cookies or this policy, we're here to help.
              </p>
              <button 
                onClick={handleContactClick}
                className="inline-flex items-center px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Contact Us
              </button>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="text-center">
            <p className="text-sm text-gray-500">
              © 2025 Manifest Illusions. This policy is part of our commitment to transparency and your privacy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;